import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
} from "@chakra-ui/react";
import MemberFormFields, {
    buildMemberFormFrom, buildMemberPayload, validateMemberForm,
} from "../forms/MemberFormFields";
import { updateGroupMember } from "../groupsApi";

/**
 * Maps backend errors to friendly French messages.
 * @param {string} raw - Raw error
 * @returns {string}
 */
function mapUpdateError(raw) {
    if (typeof raw !== "string") return "Échec de la mise à jour";
    if (raw.includes("payout") && raw.includes("100")) return "La somme des payouts dépasse 100%.";
    return raw;
}

/**
 * Submits the update payload and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitUpdate({ memberId, form, setLoading, onClose, onUpdated }) {
    const err = validateMemberForm(form, false);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        const updated = await updateGroupMember(memberId, buildMemberPayload(form));
        toast.success("Membre mis à jour");
        onUpdated?.(updated);
        onClose();
    } catch (error) {
        toast.error(mapUpdateError(error?.response?.data));
    } finally {
        setLoading(false);
    }
}

/**
 * Dialog allowing admins to edit a member's role / permissions / payout.
 * @param {Object} props - { member, onClose, onUpdated }
 */
function EditMemberDialog({ member, onClose, onUpdated }) {
    const open = Boolean(member);
    const [form, setForm] = useState(buildMemberFormFrom(member));
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm(buildMemberFormFrom(member)); }, [open, member]);

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submitUpdate({ memberId: member?.id, form, setLoading, onClose, onUpdated });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Modifier {member?.surname ? ` — ${member.surname}` : "le membre"}</ModalHeader>
                <ModalCloseButton />
                <ModalBody><MemberFormFields form={form} onChange={onChange} isCreate={false} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Enregistrement...">Enregistrer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default EditMemberDialog;
